import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import { useId } from "react";
import type { PaletteId } from "@shared/events";
import { graphemeLength } from "@shared/text";
import type { CurrencyCode } from "@shared/money";
import { formatMinor } from "@shared/money";
import { paletteStyle } from "./palette";

/**
 * OWNED BY C1. Design-system primitives (PRD §8). Plain CSS classes from src/client/styles/ui.css on
 * the tokens; no CSS-in-JS, no component library, no <select> (U-003), no text input outside Setup (U-004).
 */

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

// ---------------------------------------------------------------- BigButton

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "md" | "lg" | "xl";

export interface BigButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Full width; primary actions are full width and ≥56px tall (U-002). */
  block?: boolean;
  /** Small second line, e.g. a total under "Charge". */
  hint?: ReactNode;
  children: ReactNode;
}

export function BigButton({ variant = "secondary", size = "md", block, hint, children, type, ...rest }: BigButtonProps) {
  return (
    <button
      type={type ?? "button"}
      className={cx("wc-btn", `wc-btn--${variant}`, size !== "md" && `wc-btn--${size}`, block && "wc-btn--block")}
      {...rest}
    >
      {hint == null ? children : (
        <span className="wc-btn__stack">
          <span>{children}</span>
          <span className="wc-btn__hint">{hint}</span>
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------- Tile

export interface TileProps {
  label: ReactNode;
  /** Secondary line: price, currency name, stock. */
  sublabel?: ReactNode;
  /** Palette colour; when set the tile is painted and the label uses the palette's ink colour. */
  color?: PaletteId;
  /** Top-right slot, typically a <Badge>. */
  corner?: ReactNode;
  photoUrl?: string;
  selected?: boolean;
  square?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** aria-pressed for toggle tiles; aria-checked semantics are handled by the caller when in a radiogroup. */
  role?: "button" | "radio";
  ariaLabel?: string;
  style?: CSSProperties;
  onPointerDown?: (e: React.PointerEvent<HTMLButtonElement>) => void;
}

export function Tile({
  label, sublabel, color, corner, photoUrl, selected, square, disabled, onClick, role, ariaLabel, style, onPointerDown,
}: TileProps) {
  const labelText = typeof label === "string" ? label : "";
  const long = graphemeLength(labelText) > 12;
  const paint = color ? (paletteStyle(color) as CSSProperties) : undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      disabled={disabled}
      aria-label={ariaLabel}
      {...(role === "radio" ? { role: "radio", "aria-checked": !!selected } : { "aria-pressed": selected })}
      className={cx("wc-tile", color && "wc-tile--painted", square && "wc-tile--square", selected && "wc-tile--selected")}
      style={{ ...paint, ...style }}
    >
      {photoUrl ? <img className="wc-tile__photo" src={photoUrl} alt="" /> : null}
      <span className="wc-tile__body">
        <span className={cx("wc-tile__label", long && "wc-tile__label--long")}>{label}</span>
        {sublabel != null ? <span className="wc-tile__sub">{sublabel}</span> : null}
      </span>
      {corner != null ? <span className="wc-tile__corner">{corner}</span> : null}
    </button>
  );
}

// ---------------------------------------------------------------- Badge

export type BadgeTone = "neutral" | "ok" | "warn" | "danger";

export function Badge({ tone = "neutral", dot, children }: { tone?: BadgeTone; dot?: boolean; children: ReactNode }) {
  return (
    <span className={cx("wc-badge", tone !== "neutral" && `wc-badge--${tone}`)}>
      {dot ? <span className="wc-badge__dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------- SegmentedButtons

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
}

export interface SegmentedButtonsProps<T extends string> {
  label: string;
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  tall?: boolean;
  disabled?: boolean;
}

/** Replaces every dropdown in the app (U-003). */
export function SegmentedButtons<T extends string>({ label, options, value, onChange, tall, disabled }: SegmentedButtonsProps<T>) {
  return (
    <div role="radiogroup" aria-label={label} className={cx("wc-seg", tall && "wc-seg--tall")}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          disabled={disabled}
          className="wc-seg__btn"
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- NumberDisplay

export interface NumberDisplayProps {
  /** Integer minor units. */
  valueMinor: number;
  currency: CurrencyCode;
  label?: string;
  size?: "sm" | "md" | "lg" | "xl";
  tone?: "default" | "accent" | "danger";
  align?: "left" | "right";
  /** Prefix a sign, e.g. for variance. */
  signed?: boolean;
}

/** Big tabular numerals for totals, change, and cash on hand (U-007). */
export function NumberDisplay({ valueMinor, currency, label, size = "md", tone = "default", align = "left", signed }: NumberDisplayProps) {
  const text = formatMinor(Math.abs(valueMinor), currency);
  const sign = signed && valueMinor !== 0 ? (valueMinor > 0 ? "+" : "−") : valueMinor < 0 ? "−" : "";
  return (
    <span className={cx("wc-number", `wc-number--${size}`, tone !== "default" && `wc-number--${tone}`, align === "right" && "wc-number--right")}>
      {label ? <span className="wc-number__label">{label}</span> : null}
      <span className="wc-number__value">{sign}{text}</span>
    </span>
  );
}

// ---------------------------------------------------------------- Stepper

export function Stepper({ label, value, onChange, min = 0, max = 30 }: {
  label: string; value: number; onChange: (n: number) => void; min?: number; max?: number;
}) {
  return (
    <div className="wc-stepper" role="group" aria-label={label}>
      <button type="button" className="wc-stepper__btn" aria-label={`${label}: decrease`} disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}>−</button>
      <output className="wc-stepper__value" aria-label={`${label}: ${value}`}>{value}</output>
      <button type="button" className="wc-stepper__btn" aria-label={`${label}: increase`} disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}>+</button>
    </div>
  );
}

// ---------------------------------------------------------------- Toggle

export function Toggle({ label, description, checked, onChange, disabled }: {
  label: string; description?: ReactNode; checked: boolean; onChange: (next: boolean) => void; disabled?: boolean;
}) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} className="wc-toggle"
      onClick={() => onChange(!checked)}>
      <span className="wc-toggle__inner">
        <span className="wc-toggle__text">
          <span className="wc-toggle__title">{label}</span>
          {description ? <span className="wc-toggle__desc">{description}</span> : null}
        </span>
        <span className="wc-toggle__track" aria-hidden="true"><span className="wc-toggle__thumb" /></span>
      </span>
    </button>
  );
}

// ---------------------------------------------------------------- TextField (Setup only)

export interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Grapheme budget; shows a live counter (F-301). */
  maxGraphemes?: number;
  helper?: ReactNode;
  error?: string | null;
  placeholder?: string;
  inputMode?: "text" | "email";
  type?: "text" | "email" | "password";
  autoComplete?: string;
  prefix?: string;
}

/**
 * The only text input in the product. Allowed in Setup and the sign-in screen only (U-004, D-008);
 * the selling flow uses <Keypad>.
 */
export function TextField({
  label, value, onChange, maxGraphemes, helper, error, placeholder, inputMode = "text", type = "text", autoComplete, prefix,
}: TextFieldProps) {
  const id = useId();
  const used = maxGraphemes ? graphemeLength(value) : 0;
  const over = maxGraphemes ? used > maxGraphemes : false;
  return (
    <div className={cx("wc-field", (error || over) && "wc-field--invalid")}>
      <label className="wc-field__label" htmlFor={id}>{label}</label>
      <input
        id={id}
        className="wc-field__input"
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={prefix && value ? `${prefix}${value}` : value}
        aria-invalid={error || over ? true : undefined}
        aria-describedby={helper || error ? `${id}-help` : undefined}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(prefix && raw.startsWith(prefix) ? raw.slice(prefix.length) : raw);
        }}
      />
      {(helper || error || maxGraphemes) ? (
        <span className="wc-field__foot">
          <span id={`${id}-help`} className={error ? "wc-field__error" : undefined}>{error ?? helper}</span>
          {maxGraphemes ? <span className="wc-field__count">{used}/{maxGraphemes}</span> : null}
        </span>
      ) : null}
    </div>
  );
}
